/**
 * NovelRightsPanel — 7-field editorial rights block + legal notice.
 * LOT 3 (2026-07-27) — used by Publishers workspace and Novel detail pages.
 *
 * Contract:
 *   - `novel` object with slug/title/subtitle/isbn/isbn_en/status/status_message
 *   - `locale` — "fr" or "en"
 *   - `t` — locale bundle (fr/pro.json or en/pro.json) with `t.rights`
 *
 * Never advertises a right as available without contractual validation.
 */
import { useMemo } from "react";
const EMPTY = Object.freeze({});

function labelForStatus(status, t, locale) {
  if (!status) return locale === "en" ? "Status pending" : "Statut à préciser";
  const map = t?.novel_status || t?.status || {};
  return map[status] || status;
}

export default function NovelRightsPanel({ novel, locale, t }) {
  const rights = t?.rights || EMPTY;
  const notice = rights.independent_notice || "";
  const fields = rights.fields || EMPTY;
  const values = rights.values || EMPTY;
  const isbn = locale === "en" ? (novel.isbn_en || novel.isbn) : (novel.isbn || novel.isbn_en);
  const isbnDisplay = isbn && /^\d{10,13}$/.test(String(isbn))
    ? isbn
    : (values.isbn_unassigned || "ISBN not yet assigned");
  const isPublished = novel.status === "publie";
  // Locale-aware fallback for publication_date:
  //   - `novel.publication_date` is a factual date string (locale-agnostic, e.g. "January 2025").
  //   - `novel.status_message` is authored in French only and MUST NOT be shown in EN.
  const publicationDate = novel.publication_date
    || novel.release_date
    || novel.publication_year
    || (locale === "en"
        ? (isPublished ? "—" : "Work in progress")
        : (novel.status_message || (isPublished ? "—" : "En cours d'écriture")));

  const rows = useMemo(() => [
    { label: fields.editorial_status, value: labelForStatus(novel.status, t, locale) },
    { label: fields.publication_date, value: publicationDate },
    { label: fields.isbn, value: isbnDisplay },
    { label: fields.formats, value: novel.formats_available || (locale === "en" ? "Paperback" : "Broché") },
    { label: fields.territories, value: novel.territories_exploited || (locale === "en" ? "France + Amazon international marketplaces" : "France + marketplaces Amazon internationales") },
    { label: fields.en_translation, value: novel.en_manuscript_status
        || (novel.file_url_en ? values.en_translation_available : values.en_translation_pending) },
    { label: fields.open_rights, value: novel.rights_open_summary || values.rights_none_confirmed },
  ], [novel, locale, t, fields, values, isbnDisplay, publicationDate]);

  return (
    <div data-testid={`novel-rights-panel-${novel.slug}`} className="border border-[var(--line)] rounded-md p-6 bg-white/40">
      {isPublished && notice && (
        <p
          data-testid={`rights-independent-notice-${novel.slug}`}
          className="text-[13px] leading-relaxed text-[var(--brown)]/85 italic mb-6"
        >
          {notice}
        </p>
      )}
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-[13px]">
        {rows.map((r, i) => (
          <div key={i} data-testid={`rights-row-${i}`} className="flex flex-col">
            <dt className="text-[11px] uppercase tracking-widest text-[var(--copper)] mb-1">{r.label}</dt>
            <dd className="text-[var(--ink)]">{r.value || "—"}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
